import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, settingsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, CheckCircle2, MessageCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [whatsappSettings, setWhatsappSettings] = useState(null);
  const navigate = useNavigate();

  // Fetch WhatsApp settings from admin panel
  useEffect(() => {
    const fetchWhatsAppSettings = async () => {
      try {
        const data = await settingsAPI.getPublicSettings();
        setWhatsappSettings(data);
      } catch (error) {
        console.error('Failed to load WhatsApp settings:', error);
      }
    };

    fetchWhatsAppSettings();
  }, []);

  const handleWhatsAppClick = () => {
    if (whatsappSettings?.whatsapp_number) {
      const phone = whatsappSettings.whatsapp_number.replace(/[^0-9]/g, '');
      const message = encodeURIComponent(
        whatsappSettings.whatsapp_message || 
        'Halo Admin, saya baru saja mendaftar akun di NUTRIMUS.'
      );
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      await authAPI.register(name, email, password);
      // Show success dialog instead of immediate redirect
      setShowSuccessDialog(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
      padding: '24px 16px',
      boxSizing: 'border-box',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Soft Ambient Background Glow */}
      <div style={{
        position: 'absolute',
        top: '20%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(5, 150, 105, 0.08) 0%, rgba(248, 250, 252, 0) 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <Card style={{
        width: '100%',
        maxWidth: '440px',
        backgroundColor: '#ffffff',
        boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.10), 0 0 1px 1px rgba(15, 23, 42, 0.04)',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        position: 'relative',
        zIndex: 1
      }}>
        <CardHeader className="space-y-1 text-center" style={{ padding: '24px 24px 12px' }}>
          <div className="mx-auto mb-2 flex items-center justify-center">
            <img 
              src="/assets/nutrimus-logo.png" 
              alt="NUTRIMUS" 
              style={{ height: '70px', width: 'auto', objectFit: 'contain' }}
            />
          </div>
          <CardTitle className="text-xl font-bold tracking-tight text-slate-900">Buat Akun Baru</CardTitle>
          <CardDescription className="text-xs text-slate-500">
            Daftar akun NUTRIMUS Clinical Nutrition Recall
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-3.5" style={{ padding: '8px 24px 16px' }}>
            {error && (
              <Alert variant="destructive" className="rounded-xl py-2 px-3">
                <AlertDescription className="text-xs">{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-semibold text-slate-700">Nama Lengkap</Label>
              <Input
                id="name"
                type="text"
                placeholder="dr. John Doe, Sp.GK"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
                className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nama@rumah-sakit.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                minLength={6}
                className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" className="text-xs font-semibold text-slate-700">Konfirmasi Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                minLength={6}
                className="h-10 text-xs bg-slate-50/80 border-slate-300 focus-visible:ring-emerald-500"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3" style={{ padding: '8px 24px 24px' }}>
            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-10 rounded-xl shadow-md shadow-emerald-600/25 transition-all text-xs" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Mendaftarkan akun...
                </>
              ) : (
                'Daftar Sekarang'
              )}
            </Button>
            <p className="text-xs text-center text-slate-500">
              Sudah memiliki akun?{' '}
              <Link to="/" className="text-emerald-600 hover:text-emerald-700 hover:underline font-semibold">
                Masuk di sini
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>

      {/* Success Dialog - Professional Design */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-lg">
          {/* Header with gradient background */}
          <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-green-500 to-green-600 rounded-t-lg" />
          
          <DialogHeader className="relative">
            {/* Success Icon */}
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-lg ring-8 ring-green-50">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            
            <DialogTitle className="text-center text-2xl font-bold text-gray-900">
              Registration Successful!
            </DialogTitle>
            
            <DialogDescription className="text-center space-y-3 pt-4">
              <p className="text-base text-gray-700">
                Welcome to <span className="font-semibold text-green-600">NUTRIMUS</span>!
                <br />
                Your registration has been accepted. Please log in again.
              </p>
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="sm:justify-center gap-3 mt-6">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full sm:w-auto"
            >
              Go to Login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
